import React, { useEffect, useState } from 'react';
import { Close } from "@mui/icons-material";
import api from '../../services/api';

export const MOTIVOS_INTERRUPCAO = [
  { value: 'ambiental', label: 'Ambiental' },
  { value: 'defeso_restricao', label: 'Defeso / restrição' },
  { value: 'saude_pessoal', label: 'Saúde / pessoal' },
  { value: 'embarcacao_equipamento', label: 'Embarcação / equipamento' },
  { value: 'mercado', label: 'Mercado' },
  { value: 'outro', label: 'Outro' },
];

export const CONDICOES_AMBIENTAIS = [
  { value: 'nao_especificada', label: 'Não especificada' },
  { value: 'chuva', label: 'Chuva' },
  { value: 'vento', label: 'Vento' },
  { value: 'temporal', label: 'Temporal' },
  { value: 'cheia', label: 'Cheia' },
  { value: 'seca_nivel_baixo', label: 'Seca / nível baixo' },
  { value: 'frio', label: 'Frio' },
  { value: 'calor', label: 'Calor' },
  { value: 'condicao_agua', label: 'Condição da água' },
  { value: 'outra', label: 'Outra' },
];

export const FONTES_INFORMACAO = [
  { value: 'ficha', label: 'Ficha' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'telefone', label: 'Telefone' },
  { value: 'presencial', label: 'Presencial' },
  { value: 'outro', label: 'Outro' },
];

export const rotuloDe = (lista: { value: string; label: string }[], valor?: string | null) =>
  lista.find((x) => x.value === valor)?.label || valor || '-';

// Datas do período vêm como 'YYYY-MM-DD' (DATE no banco): formata sem passar por Date (sem fuso).
export const formatarDiaBR = (dia?: string | null) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dia || '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '-';
};

const RE_DIA = /^\d{4}-\d{2}-\d{2}$/;

// Hoje (data local do navegador) como AAAA-MM-DD.
export const hojeISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Dias inclusivos nas duas pontas (mesmo dia = 1), igual ao backend.
export const diasEntre = (inicio: string, fim: string): number | null => {
  if (!RE_DIA.test(inicio) || !RE_DIA.test(fim)) return null;
  const ms = Date.parse(`${fim}T00:00:00Z`) - Date.parse(`${inicio}T00:00:00Z`);
  if (Number.isNaN(ms) || ms < 0) return null;
  return Math.round(ms / 86400000) + 1;
};

// Dia anterior a uma data AAAA-MM-DD (sem fuso).
export const vesperaDe = (dia: string): string => {
  const d = new Date(`${dia}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};

interface InterrupcaoFormProps {
  pescadorFixo: { uid_pesca_pescador: string; nome: string };
  // Projetos a que o pescador está vinculado: se for exatamente um, já vem selecionado (como no registro de pesca).
  projetosVinculados?: string[];
  interrupcao?: any | null;
  onClose: () => void;
  onSaved: () => void;
}

const InterrupcaoForm: React.FC<InterrupcaoFormProps> = ({ pescadorFixo, projetosVinculados = [], interrupcao, onClose, onSaved }) => {
  const isEdicao = !!interrupcao;

  const [projetos, setProjetos] = useState<any[]>([]);
  const [uidProjeto, setUidProjeto] = useState<string>(
    interrupcao?.uid_pesca_projeto || (projetosVinculados.length === 1 ? projetosVinculados[0] : '')
  );
  const [dataInicio, setDataInicio] = useState<string>(interrupcao?.data_inicio || '');
  const [dataFim, setDataFim] = useState<string>(interrupcao?.data_fim || '');
  // data_fim nula = período em andamento (sem data de retorno)
  const [emAndamento, setEmAndamento] = useState<boolean>(isEdicao ? !interrupcao.data_fim : false);
  const [motivo, setMotivo] = useState<string>(interrupcao?.motivo || '');
  const [condicao, setCondicao] = useState<string>(interrupcao?.condicao_ambiental || 'nao_especificada');
  const [fonte, setFonte] = useState<string>(interrupcao?.fonte_informacao || 'ficha');
  const [relato, setRelato] = useState<string>(interrupcao?.relato || '');
  const [observacao, setObservacao] = useState<string>(interrupcao?.observacao || '');

  const [conferencia, setConferencia] = useState<{ sobreposicao: any; avisos: any[] } | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    api.get('/pesca/projetos').then((res) => setProjetos(res.data.data));
  }, []);

  const hoje = hojeISO();
  const fimParaContar = emAndamento ? hoje : dataFim;
  const dias = diasEntre(dataInicio, fimParaContar);
  const periodoInvertido = !emAndamento && !!dataInicio && !!dataFim && dataFim < dataInicio;
  const comecaNoFuturo = emAndamento && !!dataInicio && dataInicio > hoje;

  // Enquanto o usuário preenche: sobreposição (bloqueia) e registros de pesca no período (só avisa).
  useEffect(() => {
    setConferencia(null);
    const pronto = RE_DIA.test(dataInicio) && (emAndamento || (RE_DIA.test(dataFim) && dataFim >= dataInicio));
    if (!pronto) return;
    let cancelado = false;
    const t = setTimeout(() => {
      api.get(`/pesca/pescadores/${pescadorFixo.uid_pesca_pescador}/interrupcoes/conferir`, {
        params: {
          data_inicio: dataInicio,
          data_fim: emAndamento ? undefined : dataFim,
          ignorar_uid: interrupcao?.uid_pesca_interrupcao_atividade,
        },
      }).then((res) => {
        if (!cancelado) setConferencia({ sobreposicao: res.data.data.sobreposicao, avisos: res.data.data.avisos || [] });
      }).catch(() => { /* a conferência é só um adianto: o backend valida de novo ao salvar */ });
    }, 300);
    return () => { cancelado = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataInicio, dataFim, emAndamento]);

  const alternarEmAndamento = (marcado: boolean) => {
    setEmAndamento(marcado);
    if (marcado) setDataFim('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uidProjeto) return setErro('Selecione um projeto.');
    if (!dataInicio) return setErro('Informe a data inicial.');
    if (!emAndamento && !dataFim) return setErro('Informe a data final ou marque "Ainda sem atividade".');
    if (periodoInvertido) return setErro('A data final não pode ser anterior à data inicial.');
    if (!motivo) return setErro('Selecione o motivo.');
    if (!fonte) return setErro('Selecione a fonte da informação.');

    setSalvando(true);
    setErro('');
    try {
      const payload = {
        uid_pesca_pescador: pescadorFixo.uid_pesca_pescador,
        uid_pesca_projeto: uidProjeto,
        data_inicio: dataInicio,
        data_fim: emAndamento ? null : dataFim,
        motivo,
        condicao_ambiental: motivo === 'ambiental' ? condicao : null,
        fonte_informacao: fonte,
        relato: relato.trim() || null,
        observacao: observacao.trim() || null,
      };
      if (isEdicao) {
        await api.put(`/pesca/interrupcoes/${interrupcao.uid_pesca_interrupcao_atividade}`, payload);
      } else {
        await api.post('/pesca/interrupcoes', payload);
      }
      onSaved();
    } catch (err: any) {
      setErro(err?.response?.data?.message || 'Erro ao salvar o período.');
    } finally {
      setSalvando(false);
    }
  };

  const sobreposicao = conferencia?.sobreposicao;
  const avisoRegistros = conferencia?.avisos.find((a) => a.tipo === 'registro_no_periodo');
  const bloqueado = salvando || !!sobreposicao || periodoInvertido;

  const textoSobreposicao = (s: any) => {
    const periodo = s.em_andamento
      ? `desde ${formatarDiaBR(s.data_inicio)}, em andamento`
      : `de ${formatarDiaBR(s.data_inicio)} a ${formatarDiaBR(s.data_fim)}`;
    if (s.codigo === 'periodo_em_aberto_existente') {
      return `Este pescador já tem um período sem atividade em andamento (${periodo}). Encerre-o antes de abrir outro.`;
    }
    return `Este pescador já tem um período sem atividade ${periodo}, que se sobrepõe a este. Ajuste as datas ou edite o período existente.`;
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, color: 'var(--green-700, #00b37e)' }}>
            {isEdicao ? 'Editar período sem atividade' : 'Novo período sem atividade'}
          </h2>
          <Close style={{ cursor: 'pointer' }} onClick={onClose} />
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ ...fieldWrapStyle, flex: 1 }}>
              <label style={labelStyle}>Pescador</label>
              <input style={inputStyle} value={pescadorFixo.nome} disabled />
            </div>
            <div style={{ ...fieldWrapStyle, flex: 1 }}>
              <label style={labelStyle}>Projeto *</label>
              <select style={inputStyle} value={uidProjeto} onChange={(e) => setUidProjeto(e.target.value)}>
                <option value="">Selecione...</option>
                {projetos.map((p) => (
                  <option key={p.uid_pesca_projeto} value={p.uid_pesca_projeto}>{p.nome}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ ...fieldWrapStyle, flex: 1 }}>
              <label style={labelStyle}>Data inicial *</label>
              <input type="date" style={inputStyle} value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
            </div>
            <div style={{ ...fieldWrapStyle, flex: 1 }}>
              <label style={labelStyle}>Data final {emAndamento ? '' : '*'}</label>
              <input
                type="date"
                style={{ ...inputStyle, background: emAndamento ? '#f5f5f5' : undefined }}
                value={dataFim}
                min={dataInicio || undefined}
                disabled={emAndamento}
                onChange={(e) => setDataFim(e.target.value)}
              />
            </div>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 14px', fontSize: '0.9rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={emAndamento} onChange={(e) => alternarEmAndamento(e.target.checked)} />
            Ainda sem atividade (sem data de retorno)
          </label>

          {dias !== null && (
            <p style={{ margin: '0 0 14px', fontWeight: 600, color: 'var(--green-700, #00b37e)' }}>
              {dias} {dias === 1 ? 'dia' : 'dias'} sem atividade{emAndamento ? ' até hoje' : ''}
            </p>
          )}
          {comecaNoFuturo && (
            <p style={{ margin: '0 0 14px', fontSize: '0.85rem', color: '#666' }}>
              O período em andamento ainda não começou (a data inicial é futura).
            </p>
          )}
          {periodoInvertido && (
            <p style={{ ...avisoStyle, background: '#fdecee', color: '#c4283a' }}>
              A data final não pode ser anterior à data inicial.
            </p>
          )}
          {sobreposicao && (
            <p style={{ ...avisoStyle, background: '#fdecee', color: '#c4283a' }}>{textoSobreposicao(sobreposicao)}</p>
          )}
          {avisoRegistros && (
            <p style={{ ...avisoStyle, background: '#fff6e0', color: '#8a5a00' }}>
              Atenção: há {avisoRegistros.quantidade} {avisoRegistros.quantidade === 1 ? 'registro de pesca' : 'registros de pesca'} deste pescador dentro do período ({(avisoRegistros.datas as string[]).slice(0, 5).map(formatarDiaBR).join(', ')}{avisoRegistros.datas.length > 5 ? '…' : ''}). Você pode salvar mesmo assim.
            </p>
          )}

          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ ...fieldWrapStyle, flex: 1 }}>
              <label style={labelStyle}>Motivo *</label>
              <select style={inputStyle} value={motivo} onChange={(e) => setMotivo(e.target.value)}>
                <option value="">Selecione...</option>
                {MOTIVOS_INTERRUPCAO.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            {motivo === 'ambiental' && (
              <div style={{ ...fieldWrapStyle, flex: 1 }}>
                <label style={labelStyle}>Condição ambiental</label>
                <select style={inputStyle} value={condicao} onChange={(e) => setCondicao(e.target.value)}>
                  {CONDICOES_AMBIENTAIS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            )}
          </div>

          <div style={fieldWrapStyle}>
            <label style={labelStyle}>Fonte da informação *</label>
            <select style={inputStyle} value={fonte} onChange={(e) => setFonte(e.target.value)}>
              {FONTES_INFORMACAO.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>

          <div style={fieldWrapStyle}>
            <label style={labelStyle}>Relato do pescador</label>
            <textarea
              style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
              value={relato}
              onChange={(e) => setRelato(e.target.value)}
              placeholder="Ex.: não trabalhou por causa do tempo / estou em tratamento, quando voltar registro"
            />
            {motivo === 'saude_pessoal' && (
              <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#8a5a00' }}>
                Não registre diagnóstico ou detalhes de saúde, apenas o fato.
              </p>
            )}
          </div>
          <div style={fieldWrapStyle}>
            <label style={labelStyle}>Observação</label>
            <textarea
              style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
            />
          </div>

          {erro && <p style={{ color: '#f75a68', fontSize: '0.85rem' }}>{erro}</p>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
            <button type="button" onClick={onClose} style={secondaryBtnStyle}>Cancelar</button>
            <button type="submit" disabled={bloqueado} style={{ ...primaryBtnStyle, opacity: bloqueado ? 0.6 : 1 }}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000, overflowY: 'auto', padding: '20px 0',
};
const modalStyle: React.CSSProperties = {
  background: '#fff', borderRadius: '12px', padding: '24px',
  width: '90%', maxWidth: '620px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
};
const fieldWrapStyle: React.CSSProperties = { marginBottom: '14px' };
const labelStyle: React.CSSProperties = { display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '4px' };
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #ddd', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
};
const avisoStyle: React.CSSProperties = { margin: '0 0 14px', padding: '8px 10px', borderRadius: '8px', fontSize: '0.85rem' };
const primaryBtnStyle: React.CSSProperties = {
  padding: '8px 20px', borderRadius: '20px', border: 'none', background: '#03c9fe', color: '#fff', cursor: 'pointer',
};
const secondaryBtnStyle: React.CSSProperties = {
  padding: '8px 16px', borderRadius: '20px', border: '1px solid #ddd', background: '#fff', color: '#666', cursor: 'pointer',
};

export default InterrupcaoForm;
